import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Globe2, Lightbulb, Cog, DollarSign, UserIcon, HandshakeIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Define props interface for worldwidecraft
interface worldwidecraftProps {
  language?: string;
}

// Define feature structure
interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  expandedContent: string;
}

// Define translations for worldwidecraft content with "Made in Germany" theme
const TRANSLATIONS: Record<string, {
  features: Feature[];
  secondSetFeatures: Feature[];
  showMore: string;
  showLess: string;
  contactUs: string;
}> = {
  en: {
    features: [
      {
        icon: Globe2,
        title: 'GLOBAL DISTRIBUTION EXCELLENCE',
        description: 'Delivering "Made in Germany" precision to every corner of the world with unmatched efficiency.',
        expandedContent: `Our global distribution network ensures seamless delivery of high-quality German-engineered products. Key highlights include:
        • Strategically located logistics hubs in Europe, Asia, and the Americas
        • AI-optimized supply chain management
        • Carbon-neutral shipping options
        • Partnerships with leading logistics providers
        • Real-time tracking for reliability and transparency`,
      },
      {
        icon: Lightbulb,
        title: 'INNOVATIVE GERMAN DESIGN',
        description: 'Pioneering cutting-edge solutions with the ingenuity of "Made in Germany" craftsmanship.',
        expandedContent: `Our innovative designs set new industry benchmarks by blending technology and aesthetics. We achieve this through:
        • Advanced R&D centers in Germany
        • Integration of AI, IoT, and smart materials
        • Multiple patented innovations
        • Collaboration with top German engineers
        • Customizable, future-ready products`,
      },
      {
        icon: Cog,
        title: 'PRECISION ENGINEERING STANDARDS',
        description: 'Uncompromising quality and durability define our "Made in Germany" engineering ethos.',
        expandedContent: `Our precision engineering reflects German excellence in every detail. Our approach includes:
        • State-of-the-art CNC machining and robotic assembly
        • ISO 9001 and DIN standard certifications
        • Rigorous quality control processes
        • Tailored solutions for automotive, aerospace, and industrial sectors
        • Long-lasting, high-performance products`,
      },
      {
        icon: DollarSign,
        title: 'SUSTAINABLE MANUFACTURING',
        description: 'Eco-friendly production processes embodying the "Made in Germany" commitment to sustainability.',
        expandedContent: `Sustainability is at the core of our operations, ensuring minimal environmental impact. We focus on:
        • Renewable energy-powered facilities
        • Waste reduction and recycling programs
        • Green supply chain optimization
        • Compliance with global sustainability goals
        • Energy-efficient product designs`,
      },
      {
        icon: UserIcon,
        title: 'CUSTOMER-CENTRIC SOLUTIONS',
        description: 'Tailored "Made in Germany" products designed to meet diverse global client needs.',
        expandedContent: `We prioritize customer satisfaction with personalized offerings, including:
        • Dedicated support teams for each client
        • Bespoke engineering and design services
        • Proactive issue resolution
        • Long-term partnership development
        • Flexible solutions for unique requirements`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: 'TRUST IN GERMAN QUALITY',
        description: 'A legacy of reliability and integrity rooted in "Made in Germany" traditions.',
        expandedContent: `Our commitment to quality builds trust worldwide. This is upheld through:
        • Adherence to strict German manufacturing standards
        • Transparent production processes
        • Consistent delivery of durable goods
        • Ethical business practices
        • Global customer confidence in our brand`,
      },
      {
        icon: Globe2,
        title: 'TECHNOLOGICAL LEADERSHIP',
        description: 'Driving the future with "Made in Germany" advancements in technology.',
        expandedContent: `We lead the way in technological innovation with:
        • Cutting-edge 3D printing and simulation tools
        • Industry 4.0 smart manufacturing systems
        • Pioneering IoT-enabled products
        • Continuous investment in R&D
        • Solutions that shape global markets`,
      },
      {
        icon: Lightbulb,
        title: 'RESEARCH-DRIVEN INNOVATION',
        description: 'Groundbreaking "Made in Germany" advancements fueled by world-class research.',
        expandedContent: `Our R&D excellence ensures a competitive edge through:
        • Collaboration with German universities and institutes
        • Expert teams pushing technological boundaries
        • Development of next-gen products
        • Focus on energy efficiency and smart tech
        • Industry-leading patent portfolio`,
      },
      {
        icon: Cog,
        title: 'OPERATIONAL PRECISION',
        description: 'Streamlined processes reflecting "Made in Germany" efficiency and excellence.',
        expandedContent: `Our operational standards maximize quality and productivity via:
        • Lean manufacturing principles
        • Automated production workflows
        • Real-time performance monitoring
        • Continuous process optimization
        • High-efficiency output across all facilities`,
      },
      {
        icon: DollarSign,
        title: 'ECONOMIC VALUE CREATION',
        description: 'Delivering sustainable growth with "Made in Germany" economic expertise.',
        expandedContent: `We create lasting value for stakeholders through:
        • Cost-effective, high-quality production
        • Strategic market expansion
        • Robust financial planning
        • Investment in scalable innovations
        • Contribution to global economic stability`,
      },
    ],
    showMore: "Show More",
    showLess: "Show Less",
    contactUs: "Contact Us",
  },
  de: {
    features: [
      {
        icon: Globe2,
        title: 'EXZELLENZ IN GLOBALER DISTRIBUTION',
        description: 'Präzision "Made in Germany" mit unübertroffener Effizienz in jeden Winkel der Welt liefern.',
        expandedContent: `Unser globales Vertriebsnetz gewährleistet die reibungslose Lieferung hochwertiger deutscher Produkte. Highlights sind:
        • Strategisch platzierte Logistikzentren in Europa, Asien und Amerika
        • KI-optimierte Lieferkettenverwaltung
        • CO₂-neutrale Versandoptionen
        • Partnerschaften mit führenden Logistikdienstleistern
        • Echtzeit-Tracking für Zuverlässigkeit und Transparenz`,
      },
      {
        icon: Lightbulb,
        title: 'INNOVATIVES DEUTSCHES DESIGN',
        description: 'Pionierarbeit bei modernsten Lösungen mit dem Einfallsreichtum deutscher Handwerkskunst.',
        expandedContent: `Unsere innovativen Designs setzen neue Maßstäbe durch Technologie und Ästhetik. Dies erreichen wir durch:
        • Fortschrittliche F&E-Zentren in Deutschland
        • Integration von KI, IoT und intelligenten Materialien
        • Mehrere patentierte Innovationen
        • Zusammenarbeit mit Top-Ingenieuren
        • Anpassbare, zukunftssichere Produkte`,
      },
      {
        icon: Cog,
        title: 'PRÄZISIONSTECHNIK-STANDARDS',
        description: 'Kompromisslose Qualität und Langlebigkeit prägen unsere "Made in Germany" Ingenieursethik.',
        expandedContent: `Unsere Präzisionstechnik spiegelt deutsche Exzellenz wider. Unser Ansatz umfasst:
        • Modernste CNC-Bearbeitung und Roboterfertigung
        • ISO 9001- und DIN-Standard-Zertifizierungen
        • Strenge Qualitätskontrollprozesse
        • Maßgeschneiderte Lösungen für Automobil, Luftfahrt und Industrie
        • Langlebige, leistungsstarke Produkte`,
      },
      {
        icon: DollarSign,
        title: 'NACHHALTIGE FERTIGUNG',
        description: 'Umweltfreundliche Produktionsprozesse verkörpern das "Made in Germany" Engagement für Nachhaltigkeit.',
        expandedContent: `Nachhaltigkeit ist das Herzstück unserer Arbeit. Wir setzen auf:
        • Anlagen mit erneuerbaren Energien
        • Programme zur Abfallreduktion und Wiederverwertung
        • Optimierung grüner Lieferketten
        • Einhaltung globaler Nachhaltigkeitsziele
        • Energieeffiziente Produktdesigns`,
      },
      {
        icon: UserIcon,
        title: 'KUNDENZENTRIERTE LÖSUNGEN',
        description: 'Maßgeschneiderte "Made in Germany" Produkte für diverse globale Kundenbedürfnisse.',
        expandedContent: `Kundenzufriedenheit steht im Mittelpunkt mit personalisierten Angeboten wie:
        • Dedizierte Support-Teams für jeden Kunden
        • Maßgeschneiderte Ingenieur- und Design-Dienstleistungen
        • Proaktive Problemlösung
        • Langfristige Partnerschaftsentwicklung
        • Flexible Lösungen für spezielle Anforderungen`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: 'VERTRAUEN IN DEUTSCHE QUALITÄT',
        description: 'Ein Erbe von Zuverlässigkeit und Integrität, verwurzelt in "Made in Germany" Traditionen.',
        expandedContent: `Unser Qualitätsversprechen schafft weltweites Vertrauen durch:
        • Einhaltung strenger deutscher Fertigungsstandards
        • Transparente Produktionsprozesse
        • Konsistente Lieferung langlebiger Güter
        • Ethische Geschäftspraktiken
        • Globales Kundenvertrauen in unsere Marke`,
      },
      {
        icon: Globe2,
        title: 'TECHNOLOGISCHE FÜHRUNG',
        description: 'Die Zukunft gestalten mit "Made in Germany" Fortschritten in der Technologie.',
        expandedContent: `Wir führen in technologischer Innovation mit:
        • Modernsten 3D-Druck- und Simulationstools
        • Industrie 4.0 intelligente Fertigungssysteme
        • Pionierarbeit bei IoT-fähigen Produkten
        • Kontinuierliche Investitionen in F&E
        • Lösungen, die globale Märkte prägen`,
      },
      {
        icon: Lightbulb,
        title: 'FORSCHUNGSGETRIEBENE INNOVATION',
        description: 'Bahnenbrechende "Made in Germany" Fortschritte durch erstklassige Forschung.',
        expandedContent: `Unsere F&E-Exzellenz sichert Wettbewerbsvorteile durch:
        • Zusammenarbeit mit deutschen Universitäten und Instituten
        • Experten, die technologische Grenzen verschieben
        • Entwicklung nächster Produktgenerationen
        • Fokus auf Energieeffizienz und smarte Technik
        • Führendes Patentportfolio`,
      },
      {
        icon: Cog,
        title: 'BETRIEBLICHE PRÄZISION',
        description: 'Optimierte Prozesse spiegeln "Made in Germany" Effizienz und Exzellenz wider.',
        expandedContent: `Unsere Betriebsstandards maximieren Qualität und Produktivität durch:
        • Lean-Fertigungsprinzipien
        • Automatisierte Produktionsabläufe
        • Echtzeit-Leistungsüberwachung
        • Kontinuierliche Prozessoptimierung
        • Hocheffiziente Produktion in allen Werken`,
      },
      {
        icon: DollarSign,
        title: 'WIRTSCHAFTLICHE WERTSCHÖPFUNG',
        description: 'Nachhaltiges Wachstum mit "Made in Germany" wirtschaftlicher Expertise liefern.',
        expandedContent: `Wir schaffen dauerhaften Wert für Stakeholder durch:
        • Kosteneffiziente, qualitativ hochwertige Produktion
        • Strategische Marktexpansion
        • Solide Finanzplanung
        • Investitionen in skalierbare Innovationen
        • Beitrag zur globalen Wirtschaftsstabilität`,
      },
    ],
    showMore: "Mehr Anzeigen",
    showLess: "Weniger Anzeigen",
    contactUs: "Kontaktieren Sie Uns",
  },
  es: {
    features: [
      {
        icon: Globe2,
        title: 'EXCELENCIA EN DISTRIBUCIÓN GLOBAL',
        description: 'Entregando precisión "Made in Germany" a cada rincón del mundo con eficiencia inigualable.',
        expandedContent: `Nuestra red de distribución global asegura la entrega sin problemas de productos alemanes de alta calidad. Destacamos:
        • Centros logísticos estratégicos en Europa, Asia y América
        • Gestión de la cadena de suministro optimizada por IA
        • Opciones de envío neutrales en carbono
        • Alianzas con proveedores logísticos líderes
        • Seguimiento en tiempo real para fiabilidad y transparencia`,
      },
      {
        icon: Lightbulb,
        title: 'DISEÑO ALEMÁN INNOVADOR',
        description: 'Pioneros en soluciones avanzadas con la ingeniosidad de la artesanía "Made in Germany".',
        expandedContent: `Nuestros diseños innovadores establecen nuevos estándares combinando tecnología y estética. Lo logramos mediante:
        • Centros avanzados de I+D en Alemania
        • Integración de IA, IoT y materiales inteligentes
        • Múltiples innovaciones patentadas
        • Colaboración con ingenieros alemanes de élite
        • Productos personalizables y preparados para el futuro`,
      },
      {
        icon: Cog,
        title: 'ESTÁNDARES DE INGENIERÍA DE PRECISIÓN',
        description: 'Calidad y durabilidad sin compromisos definen nuestra ética de ingeniería "Made in Germany".',
        expandedContent: `Nuestra ingeniería de precisión refleja la excelencia alemana en cada detalle. Nuestro enfoque incluye:
        • Mecanizado CNC de última generación y ensamblaje robótico
        • Certificaciones ISO 9001 y DIN
        • Procesos rigurosos de control de calidad
        • Soluciones a medida para automoción, aeronáutica e industria
        • Productos duraderos y de alto rendimiento`,
      },
      {
        icon: DollarSign,
        title: 'FABRICACIÓN SOSTENIBLE',
        description: 'Procesos de producción ecológicos que encarnan el compromiso "Made in Germany" con la sostenibilidad.',
        expandedContent: `La sostenibilidad es el núcleo de nuestras operaciones, minimizando el impacto ambiental. Nos enfocamos en:
        • Instalaciones alimentadas por energía renovable
        • Programas de reducción de residuos y reciclaje
        • Optimización de la cadena de suministro verde
        • Cumplimiento de metas globales de sostenibilidad
        • Diseños de productos energéticamente eficientes`,
      },
      {
        icon: UserIcon,
        title: 'SOLUCIONES CENTRADAS EN EL CLIENTE',
        description: 'Productos "Made in Germany" adaptados a las diversas necesidades de clientes globales.',
        expandedContent: `Priorizamos la satisfacción del cliente con ofertas personalizadas, incluyendo:
        • Equipos de soporte dedicados para cada cliente
        • Servicios de ingeniería y diseño a medida
        • Resolución proactiva de problemas
        • Desarrollo de asociaciones a largo plazo
        • Soluciones flexibles para requisitos únicos`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: 'CONFIANZA EN LA CALIDAD ALEMANA',
        description: 'Un legado de fiabilidad e integridad arraigado en las tradiciones "Made in Germany".',
        expandedContent: `Nuestro compromiso con la calidad genera confianza mundial mediante:
        • Adhesión a estrictos estándares de fabricación alemanes
        • Procesos de producción transparentes
        • Entrega consistente de bienes duraderos
        • Prácticas comerciales éticas
        • Confianza global en nuestra marca`,
      },
      {
        icon: Globe2,
        title: 'LIDERAZGO TECNOLÓGICO',
        description: 'Impulsando el futuro con avances tecnológicos "Made in Germany".',
        expandedContent: `Lideramos en innovación tecnológica con:
        • Herramientas de impresión 3D y simulación de vanguardia
        • Sistemas de fabricación inteligente Industria 4.0
        • Productos pioneros habilitados para IoT
        • Inversión continua en I+D
        • Soluciones que dan forma a los mercados globales`,
      },
      {
        icon: Lightbulb,
        title: 'INNOVACIÓN IMPULSADA POR LA INVESTIGACIÓN',
        description: 'Avances revolucionarios "Made in Germany" alimentados por investigación de clase mundial.',
        expandedContent: `Nuestra excelencia en I+D asegura una ventaja competitiva mediante:
        • Colaboración con universidades e institutos alemanes
        • Equipos expertos que desafían límites tecnológicos
        • Desarrollo de productos de próxima generación
        • Enfoque en eficiencia energética y tecnología inteligente
        • Portafolio líder de patentes`,
      },
      {
        icon: Cog,
        title: 'PRECISIÓN OPERATIVA',
        description: 'Procesos optimizados que reflejan la eficiencia y excelencia "Made in Germany".',
        expandedContent: `Nuestros estándares operativos maximizan calidad y productividad mediante:
        • Principios de manufactura Lean
        • Flujos de trabajo de producción automatizados
        • Monitoreo del rendimiento en tiempo real
        • Optimización continua de procesos
        • Producción de alta eficiencia en todas las instalaciones`,
      },
      {
        icon: DollarSign,
        title: 'CREACIÓN DE VALOR ECONÓMICO',
        description: 'Crecimiento sostenible con experiencia económica "Made in Germany".',
        expandedContent: `Creamos valor duradero para los interesados mediante:
        • Producción rentable y de alta calidad
        • Expansión estratégica de mercados
        • Planificación financiera sólida
        • Inversión en innovaciones escalables
        • Contribución a la estabilidad económica global`,
      },
    ],
    showMore: "Mostrar Más",
    showLess: "Mostrar Menos",
    contactUs: "Contáctenos",
  },
  fr: {
    features: [
      {
        icon: Globe2,
        title: 'EXCELLENCE EN DISTRIBUTION GLOBALE',
        description: 'Livraison de la précision "Made in Germany" dans tous les coins du monde avec une efficacité inégalée.',
        expandedContent: `Notre réseau de distribution mondial garantit une livraison fluide de produits allemands de haute qualité. Points forts :
        • Centres logistiques stratégiques en Europe, Asie et Amériques
        • Gestion de la chaîne d’approvisionnement optimisée par IA
        • Options d’expédition neutres en carbone
        • Partenariats avec des leaders logistiques
        • Suivi en temps réel pour fiabilité et transparence`,
      },
      {
        icon: Lightbulb,
        title: 'DESIGN ALLEMAND INNOVANT',
        description: 'Pionnier des solutions avant-gardistes avec l’ingéniosité de l’artisanat "Made in Germany".',
        expandedContent: `Nos designs innovants établissent de nouvelles normes en combinant technologie et esthétique. Nous y parvenons grâce à :
        • Centres de R&D avancés en Allemagne
        • Intégration de l’IA, de l’IoT et de matériaux intelligents
        • Multiples innovations brevetées
        • Collaboration avec des ingénieurs allemands de premier plan
        • Produits personnalisables et prêts pour l’avenir`,
      },
      {
        icon: Cog,
        title: 'NORMES D’INGÉNIERIE DE PRÉCISION',
        description: 'Qualité et durabilité sans compromis définissent notre éthique "Made in Germany".',
        expandedContent: `Notre ingénierie de précision reflète l’excellence allemande dans chaque détail. Notre approche inclut :
        • Usinage CNC de pointe et assemblage robotisé
        • Certifications ISO 9001 et DIN
        • Processus rigoureux de contrôle qualité
        • Solutions sur mesure pour l’automobile, l’aéronautique et l’industrie
        • Produits durables et performants`,
      },
      {
        icon: DollarSign,
        title: 'FABRICATION DURABLE',
        description: 'Processus de production écologiques incarnant l’engagement "Made in Germany" envers la durabilité.',
        expandedContent: `La durabilité est au cœur de nos opérations, minimisant l’impact environnemental. Nous nous concentrons sur :
        • Installations alimentées par des énergies renouvelables
        • Programmes de réduction des déchets et de recyclage
        • Optimisation de la chaîne d’approvisionnement verte
        • Respect des objectifs mondiaux de durabilité
        • Conceptions de produits écoénergétiques`,
      },
      {
        icon: UserIcon,
        title: 'SOLUTIONS CENTRÉES SUR LE CLIENT',
        description: 'Produits "Made in Germany" adaptés aux divers besoins des clients mondiaux.',
        expandedContent: `Nous priorisons la satisfaction client avec des offres personnalisées, incluant :
        • Équipes de support dédiées pour chaque client
        • Services d’ingénierie et de design sur mesure
        • Résolution proactive des problèmes
        • Développement de partenariats à long terme
        • Solutions flexibles pour des besoins uniques`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: 'CONFIANCE EN LA QUALITÉ ALLEMANDE',
        description: 'Un héritage de fiabilité et d’intégrité ancré dans les traditions "Made in Germany".',
        expandedContent: `Notre engagement envers la qualité inspire la confiance mondiale grâce à :
        • Respect des normes strictes de fabrication allemande
        • Processus de production transparents
        • Livraison constante de biens durables
        • Pratiques commerciales éthiques
        • Confiance globale en notre marque`,
      },
      {
        icon: Globe2,
        title: 'LEADERSHIP TECHNOLOGIQUE',
        description: 'Façonner l’avenir avec des avancées technologiques "Made in Germany".',
        expandedContent: `Nous menons l’innovation technologique avec :
        • Outils d’impression 3D et de simulation de pointe
        • Systèmes de fabrication intelligente Industrie 4.0
        • Produits pionniers compatibles IoT
        • Investissement continu en R&D
        • Solutions qui influencent les marchés mondiaux`,
      },
      {
        icon: Lightbulb,
        title: 'INNOVATION GUIDÉE PAR LA RECHERCHE',
        description: 'Avancées révolutionnaires "Made in Germany" alimentées par une recherche de classe mondiale.',
        expandedContent: `Notre excellence en R&D assure un avantage compétitif grâce à :
        • Collaboration avec des universités et instituts allemands
        • Équipes d’experts repoussant les limites technologiques
        • Développement de produits de nouvelle génération
        • Focus sur l’efficacité énergétique et la technologie intelligente
        • Portefeuille de brevets leader dans l’industrie`,
      },
      {
        icon: Cog,
        title: 'PRÉCISION OPÉRATIONNELLE',
        description: 'Processus rationalisés reflétant l’efficacité et l’excellence "Made in Germany".',
        expandedContent: `Nos normes opérationnelles maximisent qualité et productivité via :
        • Principes de fabrication Lean
        • Flux de production automatisés
        • Surveillance des performances en temps réel
        • Optimisation continue des processus
        • Production hautement efficace dans toutes les installations`,
      },
      {
        icon: DollarSign,
        title: 'CRÉATION DE VALEUR ÉCONOMIQUE',
        description: 'Croissance durable avec une expertise économique "Made in Germany".',
        expandedContent: `Nous créons une valeur durable pour les parties prenantes grâce à :
        • Production rentable et de haute qualité
        • Expansion stratégique des marchés
        • Planification financière robuste
        • Investissement dans des innovations évolutives
        • Contribution à la stabilité économique mondiale`,
      },
    ],
    showMore: "Voir Plus",
    showLess: "Voir Moins",
    contactUs: "Contactez-Nous",
  },
  it: {
    features: [
      {
        icon: Globe2,
        title: 'ECCELLENZA NELLA DISTRIBUZIONE GLOBALE',
        description: 'Portare la precisione "Made in Germany" in ogni angolo del mondo con un’efficienza senza pari.',
        expandedContent: `La nostra rete di distribuzione globale garantisce consegne impeccabili di prodotti tedeschi di alta qualità. Punti salienti:
        • Centri logistici strategici in Europa, Asia e Americhe
        • Gestione della catena di approvvigionamento ottimizzata dall’IA
        • Opzioni di spedizione a impatto zero
        • Partnership con i principali fornitori logistici
        • Tracciamento in tempo reale per affidabilità e trasparenza`,
      },
      {
        icon: Lightbulb,
        title: 'DESIGN TEDESCO INNOVATIVO',
        description: 'Pionieri di soluzioni all’avanguardia con l’ingegnosità dell’artigianato "Made in Germany".',
        expandedContent: `I nostri design innovativi stabiliscono nuovi standard unendo tecnologia ed estetica. Lo facciamo tramite:
        • Centri di R&S avanzati in Germania
        • Integrazione di IA, IoT e materiali intelligenti
        • Molteplici innovazioni brevettate
        • Collaborazione con ingegneri tedeschi di spicco
        • Prodotti personalizzabili e pronti per il futuro`,
      },
      {
        icon: Cog,
        title: 'STANDARD DI INGEGNERIA DI PRECISIONE',
        description: 'Qualità e durata senza compromessi definiscono la nostra etica "Made in Germany".',
        expandedContent: `La nostra ingegneria di precisione riflette l’eccellenza tedesca in ogni dettaglio. Il nostro approccio include:
        • Lavorazioni CNC all’avanguardia e assemblaggio robotizzato
        • Certificazioni ISO 9001 e DIN
        • Processi rigorosi di controllo qualità
        • Soluzioni su misura per automotive, aerospaziale e industria
        • Prodotti durevoli e ad alte prestazioni`,
      },
      {
        icon: DollarSign,
        title: 'PRODUZIONE SOSTENIBILE',
        description: 'Processi produttivi ecologici che incarnano l’impegno "Made in Germany" per la sostenibilità.',
        expandedContent: `La sostenibilità è al centro delle nostre operazioni, riducendo l’impatto ambientale. Ci concentriamo su:
        • Impianti alimentati da energie rinnovabili
        • Programmi di riduzione dei rifiuti e riciclo
        • Ottimizzazione della catena di approvvigionamento verde
        • Rispetto degli obiettivi globali di sostenibilità
        • Design di prodotti efficienti dal punto di vista energetico`,
      },
      {
        icon: UserIcon,
        title: 'SOLUZIONI CENTRATE SUL CLIENTE',
        description: 'Prodotti "Made in Germany" su misura per soddisfare le diverse esigenze dei clienti globali.',
        expandedContent: `Diamo priorità alla soddisfazione del cliente con offerte personalizzate, tra cui:
        • Team di supporto dedicati per ogni cliente
        • Servizi di ingegneria e design su misura
        • Risoluzione proattiva dei problemi
        • Sviluppo di partnership a lungo termine
        • Soluzioni flessibili per esigenze uniche`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: 'FIDUCIA NELLA QUALITÀ TEDESCA',
        description: 'Un’eredità di affidabilità e integrità radicata nelle tradizioni "Made in Germany".',
        expandedContent: `Il nostro impegno per la qualità crea fiducia globale grazie a:
        • Rispetto degli standard di produzione tedeschi rigorosi
        • Processi di produzione trasparenti
        • Consegna costante di beni durevoli
        • Pratiche commerciali etiche
        • Fiducia globale nel nostro marchio`,
      },
      {
        icon: Globe2,
        title: 'LEADERSHIP TECNOLOGICA',
        description: 'Guidare il futuro con progressi tecnologici "Made in Germany".',
        expandedContent: `Siamo leader nell’innovazione tecnologica con:
        • Strumenti di stampa 3D e simulazione all’avanguardia
        • Sistemi di produzione intelligente Industria 4.0
        • Prodotti pionieristici abilitati IoT
        • Investimenti continui in R&S
        • Soluzioni che plasmano i mercati globali`,
      },
      {
        icon: Lightbulb,
        title: 'INNOVAZIONE GUIDATA DALLA RICERCA',
        description: 'Progressi rivoluzionari "Made in Germany" alimentati da ricerche di livello mondiale.',
        expandedContent: `La nostra eccellenza in R&S garantisce un vantaggio competitivo tramite:
        • Collaborazione con università e istituti tedeschi
        • Team di esperti che spingono i confini tecnologici
        • Sviluppo di prodotti di nuova generazione
        • Focus su efficienza energetica e tecnologia intelligente
        • Portafoglio di brevetti leader nel settore`,
      },
      {
        icon: Cog,
        title: 'PRECISIONE OPERATIVA',
        description: 'Processi ottimizzati che riflettono l’efficienza e l’eccellenza "Made in Germany".',
        expandedContent: `I nostri standard operativi massimizzano qualità e produttività tramite:
        • Principi di produzione snella
        • Flussi di lavoro produttivi automatizzati
        • Monitoraggio delle prestazioni in tempo reale
        • Ottimizzazione continua dei processi
        • Output ad alta efficienza in tutti gli stabilimenti`,
      },
      {
        icon: DollarSign,
        title: 'CREAZIONE DI VALORE ECONOMICO',
        description: 'Crescita sostenibile con competenza economica "Made in Germany".',
        expandedContent: `Creiamo valore duraturo per gli stakeholder tramite:
        • Produzione economica e di alta qualità
        • Espansione strategica dei mercati
        • Pianificazione finanziaria solida
        • Investimenti in innovazioni scalabili
        • Contributo alla stabilità economica globale`,
      },
    ],
    showMore: "Mostra di Più",
    showLess: "Mostra di Meno",
    contactUs: "Contattaci",
  },
  nl: {
    features: [
      {
        icon: Globe2,
        title: 'EXCELLENTIE IN WERELDWIJDE DISTRIBUTIE',
        description: 'Het leveren van "Made in Germany" precisie aan elke uithoek van de wereld met ongeëvenaarde efficiëntie.',
        expandedContent: `Ons wereldwijde distributienetwerk zorgt voor naadloze levering van hoogwaardige Duitse producten. Hoogtepunten zijn:
        • Strategisch gelegen logistieke hubs in Europa, Azië en Amerika
        • AI-geoptimaliseerd beheer van de toeleveringsketen
        • Koolstofneutrale verzendopties
        • Partnerschappen met toonaangevende logistieke aanbieders
        • Realtime tracking voor betrouwbaarheid en transparantie`,
      },
      {
        icon: Lightbulb,
        title: 'INNOVATIEF DUITS DESIGN',
        description: 'Pionieren met geavanceerde oplossingen met het vernuft van "Made in Germany" vakmanschap.',
        expandedContent: `Onze innovatieve ontwerpen stellen nieuwe industrienormen door technologie en esthetiek te combineren. Dit bereiken we door:
        • Geavanceerde R&D-centra in Duitsland
        • Integratie van AI, IoT en slimme materialen
        • Meerdere gepatenteerde innovaties
        • Samenwerking met top Duitse ingenieurs
        • Aanpasbare, toekomstbestendige producten`,
      },
      {
        icon: Cog,
        title: 'PRECISE ENGINEERING STANDAARDEN',
        description: 'Oncompromitterende kwaliteit en duurzaamheid kenmerken onze "Made in Germany" engineering-ethiek.',
        expandedContent: `Onze precisie-engineering weerspiegelt Duitse excellentie in elk detail. Onze aanpak omvat:
        • Geavanceerde CNC-bewerking en robotassemblage
        • ISO 9001- en DIN-standaardcertificeringen
        • Strenge kwaliteitscontroleprocessen
        • Oplossingen op maat voor auto-, luchtvaart- en industriële sectoren
        • Langdurige, hoogpresterende producten`,
      },
      {
        icon: DollarSign,
        title: 'DUURZAME PRODUCTIE',
        description: 'Milieuvriendelijke productieprocessen die de "Made in Germany" toewijding aan duurzaamheid belichamen.',
        expandedContent: `Duurzaamheid staat centraal in onze activiteiten, met minimale milieu-impact. We focussen op:
        • Faciliteiten aangedreven door hernieuwbare energie
        • Afvalvermindering en recycleprogramma’s
        • Optimalisatie van groene toeleveringsketens
        • Naleving van wereldwijde duurzaamheidsdoelen
        • Energie-efficiënte productontwerpen`,
      },
      {
        icon: UserIcon,
        title: 'KLANTGERICHTE OPLOSSINGEN',
        description: 'Op maat gemaakte "Made in Germany" producten voor diverse wereldwijde klantbehoeften.',
        expandedContent: `We stellen klanttevredenheid voorop met gepersonaliseerde aanbiedingen, waaronder:
        • Toegewijde ondersteuningsteams voor elke klant
        • Maatwerk engineering- en ontwerpdiensten
        • Proactieve probleemoplossing
        • Ontwikkeling van langdurige partnerschappen
        • Flexibele oplossingen voor unieke vereisten`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: 'VERTROUWEN IN DUitse KWALITEIT',
        description: 'Een erfenis van betrouwbaarheid en integriteit geworteld in "Made in Germany" tradities.',
        expandedContent: `Onze toewijding aan kwaliteit bouwt wereldwijd vertrouwen op door:
        • Naleving van strikte Duitse productienormen
        • Transparante productieprocessen
        • Consistente levering van duurzame goederen
        • Ethische zakelijke praktijken
        • Wereldwijd klantvertrouwen in ons merk`,
      },
      {
        icon: Globe2,
        title: 'TECHNOLOGISCH LEIDERSCHAP',
        description: 'De toekomst vormgeven met "Made in Germany" technologische vooruitgang.',
        expandedContent: `We leiden in technologische innovatie met:
        • Geavanceerde 3D-print- en simulatietools
        • Industrie 4.0 slimme productiesystemen
        • Pionierswerk met IoT-enabled producten
        • Continue investering in R&D
        • Oplossingen die wereldmarkten vormgeven`,
      },
      {
        icon: Lightbulb,
        title: 'ONDERZOEKSGEDREVEN INNOVATIE',
        description: 'Baanbrekende "Made in Germany" vooruitgang gevoed door wereldklasse onderzoek.',
        expandedContent: `Onze R&D-excellentie zorgt voor een concurrentievoordeel door:
        • Samenwerking met Duitse universiteiten en instituten
        • Expertteams die technologische grenzen verleggen
        • Ontwikkeling van next-gen producten
        • Focus op energie-efficiëntie en slimme technologie
        • Toonaangevend patentportfolio`,
      },
      {
        icon: Cog,
        title: 'OPERATIONELE PRECISIE',
        description: 'Gestroomlijnde processen die "Made in Germany" efficiëntie en excellentie weerspiegelen.',
        expandedContent: `Onze operationele standaarden maximaliseren kwaliteit en productiviteit via:
        • Lean-productieprincipes
        • Geautomatiseerde productie-workflows
        • Realtime prestatiemonitoring
        • Continue procesoptimalisatie
        • Hoog-efficiënte output in alle faciliteiten`,
      },
      {
        icon: DollarSign,
        title: 'ECONOMISCHE WAARDECREATIE',
        description: 'Duurzame groei leveren met "Made in Germany" economische expertise.',
        expandedContent: `We creëren blijvende waarde voor stakeholders door:
        • Kosteneffectieve, hoogwaardige productie
        • Strategische marktuitbreiding
        • Robuuste financiële planning
        • Investering in schaalbare innovaties
        • Bijdrage aan wereldwijde economische stabiliteit`,
      },
    ],
    showMore: "Toon Meer",
    showLess: "Toon Minder",
    contactUs: "Neem Contact Op",
  },
  sa: {
    features: [
      {
        icon: Globe2,
        title: 'التميز في التوزيع العالمي',
        description: 'توصيل الدقة "صنع في ألمانيا" إلى كل ركن في العالم بكفاءة لا مثيل لها.',
        expandedContent: `شبكتنا العالمية للتوزيع تضمن تسليم منتجات ألمانية عالية الجودة بسلاسة. النقاط البارزة تشمل:
        • مراكز لوجستية استراتيجية في أوروبا وآسيا والأمريكتين
        • إدارة سلسلة التوريد المحسنة بالذكاء الاصطناعي
        • خيارات شحن محايدة الكربون
        • شراكات مع مزودي الخدمات اللوجستية الرائدين
        • تتبع في الوقت الحقيقي للموثوقية والشفافية`,
      },
      {
        icon: Lightbulb,
        title: 'التصميم الألماني المبتكر',
        description: 'ريادة الحلول المتطورة ببراعة الحرفية "صنع في ألمانيا".',
        expandedContent: `تصاميمنا المبتكرة تضع معايير جديدة من خلال مزج التكنولوجيا والجماليات. نحقق ذلك عبر:
        • مراكز البحث والتطوير المتقدمة في ألمانيا
        • دمج الذكاء الاصطناعي وإنترنت الأشياء والمواد الذكية
        • ابتكارات متعددة مسجلة ببراءات اختراع
        • تعاون مع أفضل المهندسين الألمان
        • منتجات قابلة للتخصيص وجاهزة للمستقبل`,
      },
      {
        icon: Cog,
        title: 'معايير الهندسة الدقيقة',
        description: 'الجودة والمتانة دون مساومة تعرف أخلاقياتنا الهندسية "صنع في ألمانيا".',
        expandedContent: `هندستنا الدقيقة تعكس التميز الألماني في كل التفاصيل. نهجنا يشمل:
        • تصنيع CNC حديث وتجميع روبوتي
        • شهادات ISO 9001 وDIN
        • عمليات صارمة لمراقبة الجودة
        • حلول مخصصة للسيارات والفضاء والصناعة
        • منتجات طويلة الأمد وعالية الأداء`,
      },
      {
        icon: DollarSign,
        title: 'التصنيع المستدام',
        description: 'عمليات إنتاج صديقة للبيئة تجسد التزام "صنع في ألمانيا" بالاستدامة.',
        expandedContent: `الاستدامة هي جوهر عملياتنا، مما يضمن الحد الأدنى من التأثير البيئي. نركز على:
        • مرافق تعمل بالطاقة المتجددة
        • برامج تقليل النفايات وإعادة التدوير
        • تحسين سلسلة التوريد الخضراء
        • الالتزام بأهداف الاستدامة العالمية
        • تصاميم منتجات موفرة للطاقة`,
      },
      {
        icon: UserIcon,
        title: 'حلول تركز على العملاء',
        description: 'منتجات "صنع في ألمانيا" مصممة لتلبية احتياجات العملاء العالميين المتنوعة.',
        expandedContent: `نعطي الأولوية لرضا العملاء بعروض مخصصة، بما في ذلك:
        • فرق دعم مخصصة لكل عميل
        • خدمات هندسية وتصميمية مخصصة
        • حل المشكلات بشكل استباقي
        • تطوير شراكات طويلة الأمد
        • حلول مرنة لمتطلبات فريدة`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: 'الثقة في الجودة الألمانية',
        description: 'إرث من الموثوقية والنزاهة متجذر في تقاليد "صنع في ألمانيا".',
        expandedContent: `التزامنا بالجودة يبني الثقة عالميًا من خلال:
        • الالتزام بمعايير التصنيع الألمانية الصارمة
        • عمليات إنتاج شفافة
        • تسليم مستمر للسلع المتينة
        • ممارسات تجارية أخلاقية
        • ثقة العملاء العالمية في علامتنا التجارية`,
      },
      {
        icon: Globe2,
        title: 'الريادة التكنولوجية',
        description: 'دفع المستقبل بتقدم تكنولوجي "صنع في ألمانيا".',
        expandedContent: `نقود الابتكار التكنولوجي بـ:
        • أدوات طباعة ثلاثية الأبعاد ومحاكاة متطورة
        • أنظمة تصنيع ذكية للصناعة 4.0
        • منتجات رائدة مدعومة بإنترنت الأشياء
        • استثمار مستمر في البحث والتطوير
        • حلول تشكل الأسواق العالمية`,
      },
      {
        icon: Lightbulb,
        title: 'الابتكار المستند إلى البحث',
        description: 'تقدم ثوري "صنع في ألمانيا" مدعوم بأبحاث عالمية المستوى.',
        expandedContent: `تميزنا في البحث والتطوير يضمن ميزة تنافسية من خلال:
        • التعاون مع الجامعات والمعاهد الألمانية
        • فرق الخبراء التي تدفع حدود التكنولوجيا
        • تطوير منتجات الجيل القادم
        • التركيز على كفاءة الطاقة والتكنولوجيا الذكية
        • محفظة براءات اختراع رائدة في الصناعة`,
      },
      {
        icon: Cog,
        title: 'الدقة التشغيلية',
        description: 'عمليات مبسطة تعكس الكفاءة والتميز "صنع في ألمانيا".',
        expandedContent: `معاييرنا التشغيلية تزيد من الجودة والإنتاجية عبر:
        • مبادئ التصنيع الرشيق
        • تدفقات عمل إنتاجية آلية
        • مراقبة الأداء في الوقت الحقيقي
        • تحسين مستمر للعمليات
        • إنتاج عالي الكفاءة في جميع المرافق`,
      },
      {
        icon: DollarSign,
        title: 'خلق القيمة الاقتصادية',
        description: 'تقديم نمو مستدام بخبرة اقتصادية "صنع في ألمانيا".',
        expandedContent: `نخلق قيمة دائمة لأصحاب المصلحة من خلال:
        • إنتاج فعال من حيث التكلفة وعالي الجودة
        • التوسع الاستراتيجي في الأسواق
        • التخطيط المالي القوي
        • الاستثمار في الابتكارات القابلة للتطوير
        • المساهمة في الاستقرار الاقتصادي العالمي`,
      },
    ],
    showMore: "عرض المزيد",
    showLess: "عرض أقل",
    contactUs: "اتصل بنا",
  },
  hk: {
    features: [
      {
        icon: Globe2,
        title: '全球分銷卓越',
        description: '以無與倫比的效率將「德國製造」的精準傳遞到世界每個角落。',
        expandedContent: `我們的全球分銷網絡確保高品質德國工程產品的無縫交付。主要亮點包括：
        • 位於歐洲、亞洲和美洲的策略性物流中心
        • AI優化的供應鏈管理
        • 碳中和運輸選項
        • 與領先物流供應商的合作
        • 實時追蹤確保可靠性和透明度`,
      },
      {
        icon: Lightbulb,
        title: '德國創新設計',
        description: '以「德國製造」工藝的獨創性開拓尖端解決方案。',
        expandedContent: `我們的創新設計通過融合技術與美學，設定行業新標準。我們通過以下方式實現：
        • 德國先進的研發中心
        • 整合AI、物聯網和智能材料
        • 多項專利創新
        • 與頂尖德國工程師合作
        • 可定制、面向未來的產品`,
      },
      {
        icon: Cog,
        title: '精密工程標準',
        description: '不妥協的品質和耐用性定義了我們的「德國製造」工程理念。',
        expandedContent: `我們的精密工程在每個細節中體現德國卓越。我們的方法包括：
        • 最先進的CNC加工和機器人組裝
        • ISO 9001和DIN標準認證
        • 嚴格的品質控制流程
        • 汽車、航空和工業領域的定制解決方案
        • 持久、高性能的產品`,
      },
      {
        icon: DollarSign,
        title: '可持續製造',
        description: '體現「德國製造」對可持續發展承諾的環保生產流程。',
        expandedContent: `可持續性是我們運營的核心，確保最小的環境影響。我們專注於：
        • 可再生能源驅動的設施
        • 減少廢物和回收計劃
        • 綠色供應鏈優化
        • 符合全球可持續發展目標
        • 節能產品設計`,
      },
      {
        icon: UserIcon,
        title: '以客戶為中心的解決方案',
        description: '為滿足全球客戶多樣化需求量身定制的「德國製造」產品。',
        expandedContent: `我們優先考慮客戶滿意度，提供個性化服務，包括：
        • 每個客戶的專屬支持團隊
        • 定制工程和設計服務
        • 主動解決問題
        • 長期合作夥伴關係發展
        • 針對獨特需求的靈活解決方案`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: '信賴德國品質',
        description: '根植於「德國製造」傳統的可靠性和誠信遺產。',
        expandedContent: `我們對品質的承諾在全球建立信任，通過：
        • 遵循嚴格的德國製造標準
        • 透明的生產流程
        • 持續交付耐用產品
        • 道德商業實踐
        • 全球客戶對我們品牌的信心`,
      },
      {
        icon: Globe2,
        title: '技術領先',
        description: '以「德國製造」的技術進步驅動未來。',
        expandedContent: `我們在技術創新方面領先，憑藉：
        • 尖端的3D打印和模擬工具
        • 工業4.0智能製造系統
        • 開創性的物聯網產品
        • 持續的研發投資
        • 塑造全球市場的解決方案`,
      },
      {
        icon: Lightbulb,
        title: '研究驅動的創新',
        description: '由世界級研究推動的「德國製造」突破性進展。',
        expandedContent: `我們的研發卓越確保競爭優勢，通過：
        • 與德國大學和研究所合作
        • 專家團隊推動技術邊界
        • 下一代產品的開發
        • 專注於能源效率和智能技術
        • 行業領先的專利組合`,
      },
      {
        icon: Cog,
        title: '運營精準',
        description: '反映「德國製造」效率與卓越的精簡流程。',
        expandedContent: `我們的運營標準通過以下方式最大化品質和生產力：
        • 精益製造原則
        • 自動化生產流程
        • 實時性能監控
        • 持續流程優化
        • 所有設施的高效產出`,
      },
      {
        icon: DollarSign,
        title: '經濟價值創造',
        description: '以「德國製造」的經濟專業知識實現可持續增長。',
        expandedContent: `我們為利益相關者創造持久價值，通過：
        • 成本效益高、品質優良的生產
        • 策略性市場擴張
        • 穩健的財務規劃
        • 投資於可擴展創新
        • 促進全球經濟穩定`,
      },
    ],
    showMore: "顯示更多",
    showLess: "顯示更少",
    contactUs: "聯繫我們",
  },
  sg: {
    features: [
      {
        icon: Globe2,
        title: '全球分销卓越',
        description: '以无与伦比的效率将“德国制造”的精准传递到世界每个角落。',
        expandedContent: `我们的全球分销网络确保高质量德国工程产品的无缝交付。主要亮点包括：
        • 位于欧洲、亚洲和美洲的战略性物流中心
        • AI优化的供应链管理
        • 碳中和运输选项
        • 与领先物流供应商的合作
        • 实时追踪确保可靠性和透明度`,
      },
      {
        icon: Lightbulb,
        title: '德国创新设计',
        description: '以“德国制造”工艺的独创性开拓尖端解决方案。',
        expandedContent: `我们的创新设计通过融合技术和美学，设定行业新标准。我们通过以下方式实现：
        • 德国先进的研发中心
        • 整合AI、物联网和智能材料
        • 多项专利创新
        • 与顶尖德国工程师合作
        • 可定制、面向未来的产品`,
      },
      {
        icon: Cog,
        title: '精密工程标准',
        description: '不妥协的品质和耐用性定义了我们的“德国制造”工程理念。',
        expandedContent: `我们的精密工程在每个细节中体现德国卓越。我们的方法包括：
        • 最先进的CNC加工和机器人组装
        • ISO 9001和DIN标准认证
        • 严格的品质控制流程
        • 汽车、航空和工业领域的定制解决方案
        • 持久、高性能的产品`,
      },
      {
        icon: DollarSign,
        title: '可持续制造',
        description: '体现“德国制造”对可持续发展承诺的环保生产流程。',
        expandedContent: `可持续性是我们运营的核心，确保最小的环境影响。我们专注于：
        • 可再生能源驱动的设施
        • 减少废物和回收计划
        • 绿色供应链优化
        • 符合全球可持续发展目标
        • 节能产品设计`,
      },
      {
        icon: UserIcon,
        title: '以客户为中心的解决方案',
        description: '为满足全球客户多样化需求量身定制的“德国制造”产品。',
        expandedContent: `我们优先考虑客户满意度，提供个性化服务，包括：
        • 每个客户的专属支持团队
        • 定制工程和设计服务
        • 主动解决问题
        • 长期合作伙伴关系发展
        • 针对独特需求的灵活解决方案`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: '信赖德国品质',
        description: '根植于“德国制造”传统的可靠性和诚信遗产。',
        expandedContent: `我们对品质的承诺在全球建立信任，通过：
        • 遵循严格的德国制造标准
        • 透明的生产流程
        • 持续交付耐用产品
        • 道德商业实践
        • 全球客户对我们品牌的信心`,
      },
      {
        icon: Globe2,
        title: '技术领先',
        description: '以“德国制造”的技术进步驱动未来。',
        expandedContent: `我们在技术创新方面领先，凭借：
        • 尖端的3D打印和模拟工具
        • 工业4.0智能制造系统
        • 开创性的物联网产品
        • 持续的研发投资
        • 塑造全球市场的解决方案`,
      },
      {
        icon: Lightbulb,
        title: '研究驱动的创新',
        description: '由世界级研究推动的“德国制造”突破性进展。',
        expandedContent: `我们的研发卓越确保竞争优势，通过：
        • 与德国大学和研究所合作
        • 专家团队推动技术边界
        • 下一代产品的开发
        • 专注于能源效率和智能技术
        • 行业领先的专利组合`,
      },
      {
        icon: Cog,
        title: '运营精准',
        description: '反映“德国制造”效率与卓越的精简流程。',
        expandedContent: `我们的运营标准通过以下方式最大化品质和生产力：
        • 精益制造原则
        • 自动化生产流程
        • 实时性能监控
        • 持续流程优化
        • 所有设施的高效产出`,
      },
      {
        icon: DollarSign,
        title: '经济价值创造',
        description: '以“德国制造”的经济专业知识实现可持续增长。',
        expandedContent: `我们为利益相关者创造持久价值，通过：
        • 成本效益高、品质优良的生产
        • 策略性市场扩张
        • 稳健的财务规划
        • 投资于可扩展创新
        • 促进全球经济稳定`,
      },
    ],
    showMore: "显示更多",
    showLess: "显示更少",
    contactUs: "联系我们",
  },
  za: {
    features: [
      {
        icon: Globe2,
        title: 'UITNEMENDHEID IN WÊRELDWYDE VERSPREIDING',
        description: 'Lewering van "Made in Germany" presisie na elke uithoek van die wêreld met ongeëwenaarde doeltreffendheid.',
        expandedContent: `Ons wêreldwye verspreidingsnetwerk verseker naatlose aflewering van hoëgehalte Duitse produkte. Hoogtepunte sluit in:
        • Strategies geleë logistieke sentrums in Europa, Asië en die Amerikas
        • KI-geoptimaliseerde voorsieningskettingbestuur
        • Koolstofneutrale verskepingsopsies
        • Vennootskappe met toonaangewende logistieke verskaffers
        • Intydse opsporing vir betroubaarheid en deursigtigheid`,
      },
      {
        icon: Lightbulb,
        title: 'INNOVATIEWE DUitse ONTWERP',
        description: 'Pionierswerk met voorpunt-oplossings met die vernuf van "Made in Germany" vakmanskap.',
        expandedContent: `Ons innoverende ontwerpe stel nuwe industriestandaarde deur tegnologie en estetika te meng. Ons bereik dit deur:
        • Gevorderde N&O-sentrums in Duitsland
        • Integrasie van KI, IoT en slim materiale
        • Veelvuldige gepatenteerde innovasies
        • Samewerking met top Duitse ingenieurs
        • Aanpasbare, toekomsgereed produkte`,
      },
      {
        icon: Cog,
        title: 'PRESISIE-INGENIEURSSTANDAARDE',
        description: 'Onkompromieënde kwaliteit en duursaamheid definieer ons "Made in Germany" ingenieursetos.',
        expandedContent: `Ons presisie-ingenieurswese weerspieël Duitse uitnemendheid in elke detail. Ons benadering sluit in:
        • Modernste CNC-bewerking en robotmontering
        • ISO 9001- en DIN-standaard sertifisering
        • Streng kwaliteitsbeheerprosesse
        • Pasgemaakte oplossings vir motor-, lugvaart- en industriële sektore
        • Langdurige, hoëprestasie produkte`,
      },
      {
        icon: DollarSign,
        title: 'VOLHOUBARE VERVAARDIGING',
        description: 'Ekovriendelike produksieprosesse wat die "Made in Germany" verbintenis tot volhoubaarheid beliggaam.',
        expandedContent: `Volhoubaarheid is die kern van ons bedrywighede, wat minimale omgewingsimpak verseker. Ons fokus op:
        • Fasiliteite aangedryf deur hernubare energie
        • Afvalvermindering en herwinningsprogramme
        • Optimalisering van groen voorsieningsketting
        • Voldoening aan globale volhoubaarheidsdoelwitte
        • Energie-doeltreffende produkontwerpe`,
      },
      {
        icon: UserIcon,
        title: 'KLANTGESENTREERDE OPLOSSINGS',
        description: 'Pasgemaakte "Made in Germany" produkte om diverse wêreldwye kliëntebehoeftes te voldoen.',
        expandedContent: `Ons prioritiseer klantetevredenheid met gepersonaliseerde aanbiedinge, insluitend:
        • Toegewyde ondersteuningspanne vir elke kliënt
        • Pasgemaakte ingenieurs- en ontwerpdienste
        • Proaktiewe probleemoplossing
        • Langtermyn-vennootskapontwikkeling
        • Buigsame oplossings vir unieke vereistes`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: 'VERTROUE IN DUitse KWALITEIT',
        description: '’n Erfenis van betroubaarheid en integriteit gewortel in "Made in Germany" tradisies.',
        expandedContent: `Ons verbintenis tot kwaliteit bou wêreldwye vertroue deur:
        • Nakoming van streng Duitse vervaardigingstandaarde
        • Deursigtige produksieprosesse
        • Konsekwente aflewering van duursame goedere
        • Etiese besigheidspraktyke
        • Wêreldwye kliëntevertroue in ons handelsmerk`,
      },
      {
        icon: Globe2,
        title: 'TEGNOLOGIESE LEIERSKAP',
        description: 'Die toekoms dryf met "Made in Germany" vooruitgang in tegnologie.',
        expandedContent: `Ons lei die pad in tegnologiese innovasie met:
        • Voorpunt 3D-druk- en simulasie-instrumente
        • Industrie 4.0 slim vervaardigingstelsels
        • Pionierswerk met IoT-geaktiveerde produkte
        • Deurlopende investering in N&O
        • Oplossings wat globale markte vorm`,
      },
      {
        icon: Lightbulb,
        title: 'NAVORSINGSGEDREWE INNOVASIE',
        description: 'Baanbrekende "Made in Germany" vooruitgang gevoed deur wêreldklas navorsing.',
        expandedContent: `Ons N&O-uitnemendheid verseker ’n mededingende voordeel deur:
        • Samewerking met Duitse universiteite en institute
        • Kundige spanne wat tegnologiese grense verskuif
        • Ontwikkeling van volgende generasie produkte
        • Fokus op energie-doeltreffendheid en slim tegnologie
        • Bedryfsleidende patentportefeulje`,
      },
      {
        icon: Cog,
        title: 'OPERASIONELE PRESISIE',
        description: 'Gestroomlynde prosesse wat "Made in Germany" doeltreffendheid en uitnemendheid weerspieël.',
        expandedContent: `Ons operasionele standaarde maksimeer kwaliteit en produktiwiteit via:
        • Maer vervaardigingsbeginsels
        • Geoutomatiseerde produksiewerksvloei
        • Intydse prestasiemonitering
        • Deurlopende prosesoptimalisering
        • Hoë-doeltreffendheid uitset oor alle fasiliteite`,
      },
      {
        icon: DollarSign,
        title: 'EKONOMIESE WAARDESKEPPING',
        description: 'Duurzame groei lewer met "Made in Germany" ekonomiese kundigheid.',
        expandedContent: `Ons skep blywende waarde vir belanghebbendes deur:
        • Kostedoeltreffende, hoëgehalte produksie
        • Strategiese markuitbreiding
        • Robuuste finansiële beplanning
        • Investering in skaalbare innovasies
        • By dra tot globale ekonomiese stabiliteit`,
      },
    ],
    showMore: "Wys Meer",
    showLess: "Wys Minder",
    contactUs: "Kontak Ons",
  },
  kr: {
    features: [
      {
        icon: Globe2,
        title: '글로벌 유통 우수성',
        description: '비할 데 없는 효율성으로 "Made in Germany" 정밀도를 세계 곳곳에 전달합니다.',
        expandedContent: `우리의 글로벌 유통 네트워크는 고품질 독일 제품의 원활한 배송을 보장합니다. 주요 특징:
        • 유럽, 아시아, 아메리카에 전략적으로 위치한 물류 허브
        • AI로 최적화된 공급망 관리
        • 탄소 중립 배송 옵션
        • 선도적인 물류 제공업체들과의 파트너십
        • 신뢰성과 투명성을 위한 실시간 추적`,
      },
      {
        icon: Lightbulb,
        title: '혁신적인 독일 디자인',
        description: '"Made in Germany" 장인정신의 독창성으로 최첨단 솔루션을 개척합니다.',
        expandedContent: `우리의 혁신적인 디자인은 기술과 미학을 융합하여 새로운 산업 기준을 세웁니다. 이를 통해:
        • 독일의 첨단 R&D 센터
        • AI, IoT 및 스마트 재료 통합
        • 다수의 특허 혁신
        • 최고 독일 엔지니어들과의 협업
        • 맞춤형, 미래 대비 제품`,
      },
      {
        icon: Cog,
        title: '정밀 엔지니어링 표준',
        description: '타협 없는 품질과 내구성이 우리의 "Made in Germany" 엔지니어링 철학을 정의합니다.',
        expandedContent: `우리의 정밀 엔지니어링은 모든 디테일에서 독일의 우수성을 반영합니다. 접근 방식:
        • 최신 CNC 가공 및 로봇 조립
        • ISO 9001 및 DIN 표준 인증
        • 엄격한 품질 관리 프로세스
        • 자동차, 항공우주, 산업 분야 맞춤 솔루션
        • 오래 지속되는 고성능 제품`,
      },
      {
        icon: DollarSign,
        title: '지속 가능한 제조',
        description: '"Made in Germany"의 지속 가능성 약속을 구현하는 친환경 생산 프로세스.',
        expandedContent: `지속 가능성은 최소 환경 영향을 보장하며 운영의 핵심입니다. 우리는 다음에 집중:
        • 재생 가능 에너지로 운영되는 시설
        • 폐기물 감소 및 재활용 프로그램
        • 녹색 공급망 최적화
        • 글로벌 지속 가능성 목표 준수
        • 에너지 효율적인 제품 디자인`,
      },
      {
        icon: UserIcon,
        title: '고객 중심 솔루션',
        description: '다양한 글로벌 고객 요구를 충족하도록 설계된 "Made in Germany" 제품.',
        expandedContent: `우리는 개인화된 제안으로 고객 만족을 우선시합니다:
        • 각 고객을 위한 전담 지원 팀
        • 맞춤 엔지니어링 및 디자인 서비스
        • 사전 문제 해결
        • 장기 파트너십 개발
        • 독특한 요구에 대한 유연한 솔루션`,
      },
    ],
    secondSetFeatures: [
      {
        icon: HandshakeIcon,
        title: '독일 품질에 대한 신뢰',
        description: '"Made in Germany" 전통에 뿌리내린 신뢰성과 정직의 유산.',
        expandedContent: `품질에 대한 우리의 약속은 전 세계적으로 신뢰를 구축합니다:
        • 엄격한 독일 제조 표준 준수
        • 투명한 생산 프로세스
        • 내구성 있는 제품의 지속적인 제공
        • 윤리적인 비즈니스 관행
        • 우리 브랜드에 대한 글로벌 고객 신뢰`,
      },
      {
        icon: Globe2,
        title: '기술 리더십',
        description: '"Made in Germany" 기술 발전으로 미래를 이끌어갑니다.',
        expandedContent: `우리는 기술 혁신을 주도합니다:
        • 최첨단 3D 프린팅 및 시뮬레이션 도구
        • 산업 4.0 스마트 제조 시스템
        • IoT 지원 제품 개척
        • 지속적인 R&D 투자
        • 글로벌 시장을 형성하는 솔루션`,
      },
      {
        icon: Lightbulb,
        title: '연구 주도 혁신',
        description: '세계적 수준의 연구로 뒷받침된 "Made in Germany" 획기적인 발전.',
        expandedContent: `우리의 R&D 우수성은 경쟁 우위를 보장합니다:
        • 독일 대학 및 연구소와의 협력
        • 기술 경계를 넓히는 전문가 팀
        • 차세대 제품 개발
        • 에너지 효율성과 스마트 기술에 집중
        • 업계 선도적인 특허 포트폴리오`,
      },
      {
        icon: Cog,
        title: '운영 정밀성',
        description: '"Made in Germany" 효율성과 우수성을 반영하는 간소화된 프로세스.',
        expandedContent: `운영 표준은 품질과 생산성을 극대화합니다:
        • 린 제조 원칙
        • 자동화된 생산 워크플로우
        • 실시간 성능 모니터링
        • 지속적인 프로세스 최적화
        • 모든 시설에서 높은 효율성 출력`,
      },
      {
        icon: DollarSign,
        title: '경제적 가치 창출',
        description: '"Made in Germany" 경제 전문성으로 지속 가능한 성장을 제공합니다.',
        expandedContent: `우리는 이해관계자에게 지속적인 가치를 창출합니다:
        • 비용 효율적이고 고품질의 생산
        • 전략적 시장 확장
        • 견고한 재무 계획
        • 확장 가능한 혁신에 대한 투자
        • 글로벌 경제 안정에 기여`,
      },
    ],
    showMore: "더 보기",
    showLess: "덜 보기",
    contactUs: "문의하기",
  },
  jp: {
    features: [
      // Previous features omitted as per your request
    ],
    secondSetFeatures: [
      // Previous secondSetFeatures up to 'OPERATIONAL PRECISION' omitted
      {
        icon: DollarSign,
        title: '経済的価値の創出',
        description: '「メイド・イン・ジャーマニー」の経済的専門知識で持続可能な成長を提供します。',
        expandedContent: `私たちはステークホルダーに永続的な価値を創出します：
        • コスト効率が高く高品質な生産
        • 戦略的な市場拡大
        • 堅実な財務計画
        • スケーラブルなイノベーションへの投資
        • グローバルな経済安定への貢献`,
      },
    ],
    showMore: "もっと見る",
    showLess: "少なく見る",
    contactUs: "お問い合わせ",
  },
};

const FeatureCard: React.FC<{ feature: Feature; isExpanded: boolean; onToggle: () => void; language: string }> = ({
feature,
isExpanded,
onToggle,
language,
}) => {
const Icon = feature.icon;
const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];
const navigate = useNavigate();

const handleContactClick = () => {
  navigate('/contactform');
};

return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="feature-card relative bg-[#0B111F] rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 mt-10 border border-gray-700 backdrop-blur-lg overflow-hidden group flex flex-col justify-center items-center text-center"
    >
      <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
      <div className="relative flex flex-col space-y-6 items-center w-full">
        <div className="icon-wrapper bg-[#FFFFFF] p-4 rounded-lg relative">
          <div className="absolute inset-0 bg-[#FFFFFF] blur-md opacity-50" />
          <Icon className="w-8 h-8 text-[#2A3139] relative z-10" />
        </div>
        <h3 className="text-white text-xl font-bold leading-tight tracking-wide px-2">{feature.title}</h3>
        <p className="text-gray-300 leading-relaxed px-2">{feature.description}</p>
  
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: isExpanded ? 1 : 0, height: isExpanded ? 'auto' : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="text-gray-400 mb-6 leading-relaxed whitespace-pre-line px-2">{feature.expandedContent}</p>
        </motion.div>
  
        <div className="flex flex-col md:flex-row gap-3 w-full px-4 justify-center items-center">
          <button
            className="px-6 py-2 min-w-[180px] rounded-[5px] bg-[#FFFFFF] text-[#0B111F] font-medium transition-all duration-200 hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#FFFFFF] whitespace-nowrap"
            onClick={handleContactClick}
          >
            {translations.contactUs}
          </button>
          <button
            className="px-6 py-2 min-w-[180px] rounded-[5px] bg-transparent text-white font-medium border border-[#FFFFFF] transition-all duration-200 hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#0B111F] whitespace-nowrap"
            onClick={onToggle}
          >
            {isExpanded ? translations.showLess : translations.showMore}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// worldwidecraft Component
const worldwidecraft: React.FC<worldwidecraftProps> = ({ language = 'en' }) => {
const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];

return (
  <div className="max-w-7xl mx-auto space-y-0 mb-10">
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="space-y-0"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
        {translations.features.slice(0, 3).map((feature, index) => (
          <FeatureCard
            key={index}
            feature={feature}
            isExpanded={index === expandedIndex}
            onToggle={() => setExpandedIndex(index === expandedIndex ? null : index)}
            language={language}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {translations.features.slice(3, 5).map((feature, index) => (
          <FeatureCard
            key={index + 3}
            feature={feature}
            isExpanded={index + 3 === expandedIndex}
            onToggle={() => setExpandedIndex(index + 3 === expandedIndex ? null : index + 3)}
            language={language}
          />
        ))}
      </div>
    </motion.div>
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="space-y-0"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
        {translations.secondSetFeatures.slice(0, 3).map((feature, index) => (
          <FeatureCard
            key={index + 5}
            feature={feature}
            isExpanded={index + 5 === expandedIndex}
            onToggle={() => setExpandedIndex(index + 5 === expandedIndex ? null : index + 5)}
            language={language}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {translations.secondSetFeatures.slice(3, 5).map((feature, index) => (
          <FeatureCard
            key={index + 8}
            feature={feature}
            isExpanded={index + 8 === expandedIndex}
            onToggle={() => setExpandedIndex(index + 8 === expandedIndex ? null : index + 8)}
            language={language}
          />
        ))}
      </div>
    </motion.div>
  </div>
);
};

export default worldwidecraft;